/**
 * 실패한 파일의 브라우저 기록(Resource Timing)으로 고른 원인.
 * - missing: 응답 4xx — 서버에 파일이 없음(배포 스큐)
 * - server:  응답 5xx — 오리진·CDN 장애
 * - network: 응답 코드 0·2xx·3xx 인데 로드 실패 — 연결 끊김·차단(받다 끊긴 경우 포함)
 * - unknown: URL 을 못 찾음, 기록이 없음(버퍼 초과 등), responseStatus 미지원(Safari)
 */
export type ChunkFailureCause = 'missing' | 'server' | 'network' | 'unknown';

/** 인라인 스크립트가 남긴 진단값. 브라우저마다 비는 칸이 있다(Safari 는 status 없음). */
export interface ChunkFailureDiag {
  cause: ChunkFailureCause;
  status?: number;
  size?: number;
  body?: number;
  ms?: number;
  t?: number;
  vis?: string;
  online?: boolean;
  entry?: boolean;
  net?: string;
  save?: boolean;
}

const CHUNK_FAILURE_CAUSES: readonly ChunkFailureCause[] = [
  'missing',
  'server',
  'network',
  'unknown',
];

const DIAG_NUMBER_KEYS = ['status', 'size', 'body', 'ms', 't'] as const;
const DIAG_STRING_KEYS = ['vis', 'net'] as const;
const DIAG_BOOLEAN_KEYS = ['online', 'save', 'entry'] as const;

/** 손상되거나 형식이 다른 값은 버리고, 알려진 칸만 타입이 맞을 때 남긴다. */
export function parseDiag(raw: unknown): ChunkFailureDiag | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const record = raw as Record<string, unknown>;
  const cause = CHUNK_FAILURE_CAUSES.find(c => c === record.cause);
  if (!cause) return undefined;
  const diag: ChunkFailureDiag = { cause };
  for (const key of DIAG_NUMBER_KEYS) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) diag[key] = value;
  }
  for (const key of DIAG_STRING_KEYS) {
    const value = record[key];
    if (typeof value === 'string') diag[key] = value;
  }
  for (const key of DIAG_BOOLEAN_KEYS) {
    const value = record[key];
    if (typeof value === 'boolean') diag[key] = value;
  }
  return diag;
}
